import React from 'react';
import { motion } from 'framer-motion';
import { Building2, GraduationCap, Shirt, FlaskConical, Handshake, Star } from 'lucide-react';

const PartnersSection = () => {
  const partners = [
    {
      name: 'UP Student Organizations',
      description: 'University of the Philippines student orgs',
      icon: GraduationCap,
      type: 'Academic',
      color: 'neon-pink',
      borderColor: 'border-neon-pink',
    },
    {
      name: 'IECEP-PUP',
      description: 'Institute of Electronics Engineers',
      icon: FlaskConical,
      type: 'Professional',
      color: 'trophy-gold',
      borderColor: 'border-trophy-gold',
    },
    {
      name: 'Alaundry Company Inc.',
      description: 'Professional laundry services',
      icon: Shirt,
      type: 'Industry',
      color: 'success-green',
      borderColor: 'border-success-green',
    },
    {
      name: 'Philippine Science High School',
      description: 'PSHS System',
      icon: GraduationCap,
      type: 'Academic',
      color: 'cyan-400',
      borderColor: 'border-cyan-400',
    },
  ];

  return (
    <section id="partners" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Handshake className="w-6 h-6 text-lavender" />
            <h2 className="font-pixel text-lg sm:text-xl md:text-2xl text-foreground">
              PARTNERS
            </h2>
            <Handshake className="w-6 h-6 text-lavender" />
          </div>
          <p className="font-terminal text-lg sm:text-xl text-lavender max-w-2xl mx-auto">
            We collaborate with schools, orgs, and operating companies to enable
            <span className="text-success-green"> ethical data collection</span> and
            <span className="text-trophy-gold"> fair opportunities</span> for Side Questers.
          </p>
        </motion.div>

        {/* Partners Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {partners.map((partner, index) => (
            <motion.div
              key={partner.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className={`arcade-panel rounded-lg overflow-hidden border-t-4 ${partner.borderColor} group`}
            >
              <div className="p-6 text-center">
                {/* Partner Type Badge */}
                <div className="mb-4">
                  <span className={`inline-block font-pixel text-[8px] px-3 py-1 rounded bg-${partner.color}/20 text-${partner.color} border border-${partner.color}/50`}>
                    {partner.type}
                  </span>
                </div>

                {/* Icon */}
                <motion.div
                  className={`w-16 h-16 mx-auto mb-4 bg-${partner.color}/20 rounded-lg flex items-center justify-center border-2 ${partner.borderColor}/50 group-hover:border-${partner.color} transition-colors`}
                  whileHover={{ rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.4 }}
                >
                  <partner.icon className={`w-8 h-8 text-${partner.color}`} />
                </motion.div>

                {/* Name */}
                <h3 className="font-pixel text-[10px] text-foreground mb-2">
                  {partner.name}
                </h3>

                {/* Description */}
                <p className="font-terminal text-sm text-lavender">
                  {partner.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <p className="font-terminal text-base text-muted">
            <Building2 className="inline-block w-4 h-4 mr-2" />
            Interested in partnering with us? <a href="#submit" className="text-neon-pink hover:underline">Get in touch</a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnersSection;
